import Link from "next/link";
import React from "react";

const TermsPage = () => {
  return (
    <div className="container">
      <div className="main terms-and-conditions">
        <div className="title">Terms and Conditions (&quot;Terms&quot;)</div>
        Last updated: 19/11/2024
        <br />
        <br />
        This document (the “Terms”) governs the relationship between Jammable
        Limited (“Jammable”, “us”, “we” or “our”) and you in relation to your
        access and use of the online platform for generating AI voiceovers,
        currently located at https://www.jammable.com/ (together with any other
        Jammable websites and technologies owned by us, referred to together as
        the “Jammable Platform”). Jammable provides a suite of AI voice
        manipulation tools that enable users of the Jammable Platform to:
        <ul>
          <li style={{ listStyleType: "disc" }}>
            Upload a vocal file (“Dataset”) to train and create a synthetic AI
            voice model that imitates the voice contained in such Dataset
            (“Custom Voice Model”);
          </li>
          <li style={{ listStyleType: "disc" }}>
            Convert text or audio into an audio file in the voice of one of our
            Custom Voice Models; “Conversions”);
          </li>
          <li style={{ listStyleType: "disc" }}>
            Upload audio files for audio manipulation, including extracting
            vocals from audio files containing instrumentals or removing reverb
            from vocal recordings; and{" "}
          </li>
        </ul>
        such other tools, features and functionalities as Jammable may make
        available from time to time (referred to together as the “Services”). We
        refer to everyone who accesses or uses the Jammable Platform or the
        Services as a “User”.
        <br />
        <br />
        Our registered office is at Kalculus, 119 Marylebone Road, London,
        United Kingdom, NW1 5PU and our registered number is 14797573. The Terms
        form a legally binding agreement between you and us. Please take time to
        read them carefully.
        <br />
        <br />
        <div className="subtitle">1. Your acceptance of these terms</div>
        1.1 By accessing and using the Jammable Platform, and the Services made
        available through it, you agree to be bound by these Terms, as may be
        amended or varied from time to time. Your acceptance is on your behalf.
        For the purpose of these Terms, “you” and “your” means you as the User
        of the Jammable Platform and the Services.
        <br />
        <br />
        1.2 You also acknowledge that our Privacy Notice and Cookie Notice (in
        each case, as amended from time to time) apply to our use of your
        personal data and cookies (and other similar technologies) in relation
        to the Jammable Platform. Please review these Terms, our Privacy Policy
        and our Cookie Policy and save a copy of them for your future reference.
        <br />
        <br />
        1.3 If you have any questions about these Terms, please contact us using
        the contact details at the end of these Terms.
        <div className="subtitle">2. Our Right to update these terms</div>
        2.1 We may amend or vary these Terms at any time and for any reason,
        including to reflect changes affecting the Jammable Platform, our
        Services, our technology, our licensing arrangements, our payment
        policies, best practices, relevant laws and/or regulatory requirements.
        <br />
        <br />
        2.2 Where we make changes to these Terms in an adverse way to you
        (unless such change is to comply with applicable law or regulation), we
        will notify you in advance and give you the opportunity to cancel your
        subscription if you are unhappy with those changes.
        <br />
        <br />
        2.3 Any revised terms or policies will be made available to you at here
        and a notification of any material changes will be emailed to you.
        Please take a moment to read the changes. If you continue to use the
        Jammable Platform after we make the changes, you will be deemed to agree
        to the changes. If you do not agree to the changes, you should not use
        the Jammable Platform.
        <br />
        <br />
        2.4 This version of the Terms is effective from the date specified at
        the top of this page. Previous versions of the Terms can be found{" "}
        <Link href="/terms/2023-01-10">here</Link>.
        <div className="subtitle">3. Registering your account</div>
        3.1 In order to use or access certain parts of the Jammable Platform you
        may be required to register for an account (an “Account”) by providing
        certain personal information about yourself, a username and password of
        your choosing (“Registration Data”). Our services are not directed to
        children under the age of 13, and you must confirm that you are 13 years
        or older to create an Account and use the Services. If you are under 18
        years old, you must have the consent of your parent or guardian to
        access and use the Jammable Platform. You should review these Terms with
        them. If you are a parent or guardian of a User who is under 18, by
        allowing them to use the Jammable Platform, you agree to be bound by
        these Terms and you are responsible for their use of the Platform.{" "}
        <br />
        <br />
        3.2 All Users must only use the Services for domestic and private use.
        You agree not to use our Services for any commercial or business
        purposes.
        <br />
        <br />
        3.3 You shall ensure that your Registration Data, and any other
        information or data (including personal data) that you provide to us
        (“User Data”) in relation to your Account, is true and accurate, and
        kept up to date. We will use your personal data as set out in our
        Privacy Policy.
        <br />
        <br />
        3.4 You are responsible for your use of the Jammable Platform or any
        other use that you have authorised. You are also responsible for keeping
        your Registration Data secure and protecting it against unauthorised use
        and for ensuring the security of any device from which you sign into
        your Account. If you fail to do so, you are responsible for any
        unauthorised use of the Jammable Platform using your Registration Data,
        and we shall have no liability to you in respect of such use. We
        recommend that you change your password from time to time to assist with
        security.
        <br />
        <br />
        3.5 If you believe someone has accessed the Jammable Platform using your
        username and password without your authorisation, it is your
        responsibility to set up a new password. You agree to immediately notify
        us of any unauthorised use of your Registration Data. <br />
        <br />
        3.6 User names and profile images must not: (i) be obscene or offensive,
        (ii) infringe any third party rights, (iii) mislead anyone as to your
        identity, or (iv) otherwise (in our absolute discretion) be considered
        inappropriate. We may ask you to change any element of your username or
        profile image at any time, or change it directly ourselves (in which
        case we will send a message to your registered email address explaining
        this).
        <div className="subtitle">4. Subscription tiers</div>
        Jammable offers several subscription tiers for Users (Credits, Creator
        or Power User). Some subscription tiers may have differing features,
        conditions and limitations, which will be disclosed to you prior to when
        you sign up or in other communications made available to you. You can
        find specific details regarding the different subscription tiers{" "}
        <Link href="/pricing">here</Link>.
        <div className="subtitle">5. Subscription fees</div>
        5.1 Access to the Jammable Platform and the Services is conditional on
        the payment of the fees in relation to your applicable subscription tier
        (“Subscription Fees”).
        <br />
        <br />
        5.2 By registering for a subscription tier you agree that:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            to use the Services you must provide us with a current, valid method
            of payment accepted by us (“Payment Method”), as may be updated from
            time to time;
          </li>
          <li>
            we will charge a Subscription Fee to your Payment Method when you
            sign up to the Service and monthly thereafter, unless you cancel
            your subscription before your monthly payment date. Please visit the{" "}
            <a href="https://www.jammable.com/settings">Settings</a> page to see
            your next payment date.; and
          </li>
          <li>
            you authorise us to charge the Subscription Fee for the next billing
            cycle to your Payment Method.
          </li>
        </ol>
        5.3 We may change the prices that apply to our subscription tiers and
        Services. We will provide you with at least 30 days’ prior written
        notice of any changes to our pricing. If you do not agree to the price
        change or any other changes to your subscription plan, you can cancel
        your subscription before such change takes effect.
        <br />
        <br />
        5.4 If we can’t charge your Payment Method because it has expired, you
        have insufficient funds or for any other reason, and you don’t cancel
        your subscription, we might restrict your access to our Services and
        continue to try to charge you until a valid Payment Method is charged.
        Some Payment Methods might incur additional charges, such as foreign
        transaction fees or other fees related to payment processing. The taxes
        you pay might differ based on your chosen Payment Method or location.
        Please consult your payment service provider for more information.
        <br />
        <br />
        5.5 You can update your Payment Methods by visiting the{" "}
        <a href="https://www.jammable.com/settings">Settings</a> page. Following
        any such change, you authorise us to charge the updated Payment Method.
        <div className="subtitle">6. Subscription cancellation</div>
        6.1 Your subscription to our Services will continue on a rolling monthly
        basis until terminated by us or cancelled by you. To cancel your
        subscription, log into your Account and follow the instructions for
        cancellation on the Settings page.
        <br />
        <br />
        6.2 You can cancel your subscription at any time, and you will continue
        to have access to the Services until the end of the relevant billing
        period. If you cancel your subscription, your Account will automatically
        close at the end of your current billing period.{" "}
        <div className="subtitle">7. Your right to change your mind</div>
        7.1 You have 14 days after the date we confirm your subscription order
        to change your mind about a purchase (“Cancellation Period”). However,
        you lose your right to cancel your subscription once you start to use
        the Services (see more details about this in section 7.3). The
        Cancellation Period will expire 14 days after you accept these Terms.
        <br />
        <br />
        7.2 If you change your mind and want to cancel these Terms, contact our
        Support Team at support@jammable.com. To meet the cancellation deadline,
        it is sufficient for you to send your cancellation request before the
        cancellation period has expired.
        <br />
        <br />
        7.3 When you register for a subscription tier, you will be asked to tick
        a box to confirm that you consent for us to supply the Services to you
        during the Cancellation Period and that you acknowledge that this means
        you lose your right to cancel your subscription once you start to use
        the Services.
        <br />
        <br />
        7.4 This means that you lose your right to cancel this contract once you
        start to use the Services by uploading audio files or converting text or
        audio and will only be entitled to a refund in accordance with our
        Refund Policy.
        <div className="subtitle">8. Termination or suspension by Jammable</div>
        8.1 We may, at our sole discretion, without notice and without liability
        to you, immediately terminate or suspend your Account, or block your use
        of the Jammable Platform, in the event that we reasonably consider that:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>you have violated these Terms; or</li>
          <li>you have violated any of our policies.</li>
        </ol>
        8.2 If we terminate your Account on the grounds set out in section 8.1,
        you shall not be entitled to any refunds.
        <br />
        <br />
        8.3 If your Account is suspended, you will not be able to use the
        Jammable Platform during any period of suspension.
        <br />
        <br />
        8.4 We may also terminate or suspend your Account:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            if we decide to no longer provide the Jammable Platform, for example
            due to technical, financial or legitimate business reasons; or
          </li>
          <li>for any reason on 30 days’ notice to you.</li>
        </ol>
        8.5 If we terminate your Account on the grounds set out in section 8.4,
        you shall be entitled to a refund in accordance with our Refund Policy.
        <br />
        <br />
        8.6 You also acknowledge that we may cease to make the Platform
        available in any particular territory or region at any time without
        notice to you.
        <div className="subtitle">
          9. Uploading content to the Jammable Platform and generating
          Conversions
        </div>
        9.1 As part of the Service, Jammable provides access to Custom Voice
        Models generated by other Users. These Custom Voice Models may bear
        certain resemblances to the vocal characteristics of identifiable public
        figures or fictional characters. However, it is important to clarify
        that these Custom Voice Models are completely artificial creations,
        developed through the application of advanced machine learning
        algorithms. We do not claim any ownership, affiliation, or
        representation of these public figures or fictional characters whose
        vocal characteristics Custom Voice Models (generated by Jammable) may
        resemble. Furthermore, the creation and provision of these Custom Voice
        Models do not constitute any endorsement, sponsorship, or partnership
        between Jammable and the said public figures or fictional characters.
        All trademarks, names, and likenesses associated with these public
        figures or fictional characters are their respective properties.
        <br />
        <br />
        9.2 You may upload details about your Custom Voice Models including (but
        not limited to) its name, description, tags or thumbnail images (“Voice
        Model Information”). You are fully responsible for keeping all Voice
        Model Information true, accurate and complete at all times.
        <br />
        <br />
        9.3 You agree that Jammable is not responsible for the accuracy or
        completeness of any Voice Model Information, and that Jammable makes no
        representation or warranties regarding the accuracy, completeness or
        fitness for purpose of any Voice Model Information or Conversions.
        <br />
        <br />
        9.4 Jammable is not responsible for the results obtained through use of
        the Services, including any Conversions. The User is responsible for
        using the Services including:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            any results produced by the Services, including any Conversions, and
          </li>
          <li>
            assessing the adequacy, reliability, safety, accuracy, completeness
            and compliance with applicable laws and industry standards, and
            other characteristics of the Conversions.
          </li>
        </ol>
        9.5 As part of the Services, Jammable may provide access to Custom Voice
        Models as developed by other Users from time to time and allow the User
        to upload its own Custom Voice Models (“User Library”) to the Jammable
        Platform, which may then be used to generate Conversions.. You may
        choose to make your User Library available to other Users or keep it
        private.
        <div className="subtitle">
          10. Using the Jammable platform and services
        </div>
        10.1 To access the Jammable Platform, you warrant and agree that:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            you have the legal right, power and authority to agree to these
            Terms and that these Terms constitute legal, valid and binding
            obligations enforceable against you;{" "}
          </li>
          <li>
            you will comply with Jammable’s notices, guidelines and other rules
            that apply to your access to the Jammable Platform and use of the
            Services, in each case as Jammable may issue from time to time and
            notify to you via the Platform or by e-mail;
          </li>
          <li>
            you are responsible for making all arrangements necessary to access
            the Jammable Platform. You are responsible for ensuring that your
            computer and/or portable device is compatible with our Jammable
            Platform;
          </li>
          <li>
            you will access the Jammable Platform and use the Services for
            lawful purposes only and, in your use of the Jammable Platform and
            the Services, you will comply with all applicable laws;{" "}
          </li>
          <li>
            you will not make excessive or inappropriate use of the Jammable
            Platform or the Services and will not interfere with another User’s
            use or enjoyment of the Platform or the Services in any manner that
            could damage, disable, overburden or impair the Service. You
            acknowledge that the Jammable Platform and the Services are a shared
            service provided to a community of Users. Therefore, excessive or
            inappropriate use by one User may adversely affect the experience of
            other Users;
          </li>
          <li>
            your agreement with your mobile network and/or internet service
            provider (“Internet Providers”) will apply in respect of your use of
            data in connection with your use of the Jammable Platform and that
            you may be charged by your Internet Provider in respect of such use.
            You accept responsibility for such charges;
          </li>
          <li>
            all information provided by you, including Your Content, to Jammable
            AI is true, accurate and complete at all times. If there are any
            changes to the information you have previously provided to Jammable
            AI, you will promptly update any such information within your
            Account or provide to Jammable, as applicable;
          </li>
          <li>
            you have all the necessary rights, consents and permissions to
            upload Your Content to the Jammable Platform and use it in
            connection with the Services, and such information does not violate
            the intellectual property rights or other rights of any third party;
          </li>
          <li>
            except as set out in section 14.5 of these Terms, you will not use,
            or cause others to use, any automated system or software to extract
            content or data from the Jammable Platform for commercial or
            non-commercial purposes, except where you or any applicable third
            party has entered into a written agreement with us that permits such
            activity;
          </li>
          <li>
            you will not interfere with, or disrupt, the Jammable Platform or
            any servers or networks connected to the Jammable Platform,
            including by transmitting any worms, viruses, malware, spyware or
            any other code of a destructive, malicious or disruptive nature. You
            may not inject content or code or otherwise alter or interfere with
            the way any page of the Jammable Platform is rendered or displayed
            in a User’s browser or device;
          </li>
          <li>
            you will not access the Jammable Platform via a means not authorised
            in writing in advance by us including, but not limited to, automated
            devices, scripts, bots, spiders, crawlers or scrapers (except for
            standard search engine technologies);
          </li>
          <li>
            you will not impersonate any other person (including another User’s
            account) or conduct yourself in a manner which is unlawful or
            offensive, or which may harass, defame, bully or intimidate any
            other person;
          </li>
          <li>
            you will not use false email addresses or any other means to mask
            your identity;
          </li>
          <li>
            you will not use the Jammable Platform for any illegal or
            unauthorised purpose or in any way that advocates, promotes or
            assists any unlawful act such as (by way of example only) copyright
            infringement, computer misuse, or collecting or harvesting any
            information or data from our systems or servers;
          </li>
          <li>
            you will not use the Jammable Platform in any way that (i) is
            defamatory of any other person, (ii) is obscene or offensive, (iii)
            promotes discrimination based on race, sex, religion, nationality,
            disability, sexual orientation or age, or (iv) is likely to harass,
            upset, embarrass or alarm any other person;
          </li>
          <li>
            you will not change, modify, adapt or alter the Jammable Platform or
            change, modify or alter another website so as to inaccurately imply
            an association with the Jammable Platform or us;
          </li>
          <li>
            you will not circumvent, remove, alter, deactivate, degrade or
            thwart any of the content or protections in the Jammable Platform
            and/or Services or any part thereof;
          </li>
          <li>
            you will not copy, modify, decompile or reverse engineer any part of
            the Jammable Platform (including any of our software, products,
            processes accessible via the Services) including by attempting to
            derive the source code of any part of the Jammable Platform;{" "}
          </li>
          <li>
            you will not act in a manner that may be perceived as damaging to us
            or the Jammable Platform’s reputation and goodwill, or which may
            bring us or the Jammable Platform into disrepute or harm; and{" "}
          </li>
          <li>
            you will not make a derivative work of the Jammable Platform, or use
            the Jammable Platform to develop any service or product that is the
            same as, or substantially similar to, or that competes with the
            Platform.
          </li>
        </ol>
        <div className="subtitle">11. Availability of the platform</div>
        11.1 We provide the Jammable Platform on the basis that Users must only
        use the Services for domestic and private use (not for any commercial or
        business purposes). We will exercise reasonable care and skill and act
        with professional diligence in providing the Services. To the extent
        permitted by applicable law, we give no warranty that, but will take
        reasonable steps to ensure that: the Jammable Platform will be free of
        defects and/or faults; defects will be corrected; and the Jammable
        Platform or the server that makes it available are free of viruses or
        anything else which may be harmful or destructive.
        <br />
        <br />
        11.2 We will do our best to make sure that our Jammable Platform (and
        any email that we sent to you in connection with the Jammable Platform)
        is accurate, reliable, up-to-date and free from bugs, trojan horses or
        other harmful components.
        <br />
        <br />
        11.3 We provide all content available on the Jammable Platform in such
        format types as we determine.
        <div className="subtitle">
          12. Your content on the Jammable platform
        </div>
        12.1 The Jammable Platform enables you to: (i) upload User Data, Custom
        Voice Models, audio files, and other data and materials including Voice
        Model Information (“Your Content”); and (ii) generate Conversions. We
        may require that you upload Your Content in format types that we support
        via the Jammable Platform and change such formats from time to time. You
        represent and warrant that you have all rights, permissions, and
        consents necessary to upload Your Content to the Service.
        <br />
        <br />
        Your Content must not:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            be or cause you to use the Jammable Platform or the Services in any
            way that is prohibited under or in breach of these Terms;
          </li>
          <li>
            contain any material which is or may reasonably be considered to be
            threatening, defamatory, obscene, indecent, offensive, pornographic,
            abusive, liable to incite racial hatred, discriminatory, menacing,
            scandalous, inflammatory, blasphemous, or in breach of confidence;
          </li>
          <li>
            be illegal or infringe any of the rights of any third party, in any
            country in the world; and
          </li>
          <li>
            be technically harmful (by way of example it must not include any
            computer viruses, logic bombs, Trojan horses, worms, harmful
            components, corrupted data or other malicious software or harmful
            data),
          </li>
        </ol>
        together, “Infringing Content”. 12.3 We will not be responsible, or
        liable to any third party, for the content or accuracy of Your Content
        posted by you or any other User of the Jammable Platform.
        <br />
        <br />
        12.4 We reserve the right to monitor and review Your Content and the
        content of other Users, but do not have the obligation to do so. You
        acknowledge that, unless expressly stated otherwise, Your Content has
        not been verified or approved by us.
        <br />
        <br />
        12.5 We make no guarantee that Your Content will be kept on the Jammable
        AI Platform for any length of time, and we reserve the right to remove
        Your Content as we see fit, including if it does not comply with these
        Terms or removal is otherwise desirable for business or operational
        reasons. You are urged to keep back-up copies of any of Your Content
        submitted to us. The Jammable Platform should not be used as your
        primary method to store or curate copies of your images, videos, text
        and Conversions, as Your Content may be removed permanently from the
        Jammable Platform at any time, and we may not keep copies of Your
        Content.
        <br />
        <br />
        12.6 You will notify us without delay by emailing support@jammable.com
        if you become aware that any of Your Content is or becomes Infringing
        Content (as defined in section 12.2 of these Terms) and you will provide
        us with any and all information and assistance that we require to in
        order to identify and remove any Infringing Content from the Jammable
        Platform.
        <div className="subtitle">13. Deleting your account</div>
        13.1 You can ask us to delete your Account at any time by navigating to
        your <a href="https://www.jammable.com/settings">Settings</a> page
        settings and requesting that we delete your Account.
        <br />
        <br />
        13.2 We will delete Your Content within 30 days of receiving your
        request but we may retain relevant metadata (such as credit ledger
        history) on our systems for accounting and audit reasons for as long as
        is necessary to fulfil our legal and/or contractual obligations and/or
        for other legal purposes.
        <br />
        <br />
        13.3 Once we have deleted Your Content, you have no right for it to be
        recovered. We recommend that you keep a copy of all of Your Content. If
        you wish to obtain a copy of Your Content prior to deletion, please
        request access to your data in accordance with your rights set out in
        our <a href="https://www.jammable.ai/privacy">Privacy Policy.</a>
        <br />
        <br />
        13.4 When you delete your Account, your agreement with us will be
        immediately terminated.
        <div className="subtitle">14. Intellectual property rights</div>
        14.1 You acknowledge that Jammable (or its licensors and suppliers) own
        all intellectual property rights in and to Jammable Platform, Services,
        and any material, documents, information, data, reports methodologies
        and knowhow made available to you by Jammable, and all modifications,
        adaptations and enhancements to the foregoing (“Jammable IP”).
        <br />
        <br />
        14.2 As between You and Jammable, you own the Conversions that you
        generate using our Services, provided they were created in accordance
        with these Terms. Jammable makes no representations or warranties with
        respect to the current law that might apply to you and the ownership
        status of the Conversions. Due to the nature of artificial intelligence
        technologies, Conversions may also not be unique across Users and the
        Services may generate the same or similar results across Users.
        <br />
        <br />
        14.3 By uploading Your Content to the Jammable Platform, you hereby
        grant us and other Users of the Jammable Platform a worldwide,
        irrevocable, non-exclusive, transferable, royalty-free licence (which is
        unlimited in time) to:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            use, host and store all of Your Content to operate and provide the
            necessary functionality of the Jammable Platform and the Services;
          </li>
          <li>
            publicly display, broadcast, transmit, and make available Your
            Content to Users via the Jammable Platform in accordance with these
            Terms (except where Your Content is any Custom Voice Model you have
            uploaded for private use);
          </li>
          <li>
            copy, modify, translate and make derivative works of Your Content
            for use in connection with the promotion of the Jammable Platform
            (except where Your Content is any Custom Voice Model you have
            uploaded for private use);
          </li>
          <li>
            to publicly display, broadcast, transmit, and make available Your
            Content (including in the form modified by us as described in point
            (c) above) to promote us, the Jammable Platform, the User Library,
            in any media (including via our social media channels, in our
            advertising and in promotional materials), except where Your Content
            is any Custom Voice Model you have uploaded for private use; and
          </li>
          <li>
            to use Your Content for any other purposes as may be required by
            law.
          </li>
        </ol>
        14.4 Other than as expressly set out in these Terms, we do not grant you
        any right in respect of the Jammable IP or any other materials. Any
        rights not expressly granted to you in these Terms are hereby reserved.
        <br />
        <br />
        Conditional on your compliance with these Terms, Jammable grants you a
        non-exclusive, non-transferable, non-sublicensable, revocable, limited
        licence to access and use Jammable IP solely to the extent necessary to
        receive the benefit of the Services (which shall not include the use of
        the Service by, or for the benefit of, any person other than yourself).
        <div className="subtitle">15. Confidentiality</div>
        15.1 As part of the Services, you may be given access to non-public
        information that Jammable or its affiliates or third parties designate
        as confidential or should reasonably be considered confidential under
        the circumstances, including software, specifications, and other
        non-public business information (“Confidential Information”). You will
        not use, disclose, exploit, copy or modify such Confidential Information
        except as necessary to receive the benefit of the Services. You will
        keep Confidential Information secret and secure and apply at least the
        same level of protection as you use in respect of your own confidential
        or proprietary information of similar nature, using at least reasonable
        care.
        <br />
        <br />
        15.2 The obligations of confidentiality in this section do not extend to
        any information that:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            is in, or has become part of, the public domain other than as a
            result of a breach of the obligations of confidentiality under these
            Terms;
          </li>
          <li>
            was developed by you independently of any Confidential Information
            disclosed by Jammable;
          </li>
          <li>
            was independently disclosed to you by a third party entitled to
            disclose the same; or
          </li>
          <li>is legally required to be disclosed under any applicable law.</li>
        </ol>
        <div className="subtitle">16. Links to third party properties</div>
        16.1 The Jammable Platform (and emails that we send to you in connection
        with the Jammable Platform) may include links to third party websites,
        applications and/or other digital properties (“Third Party Properties”)
        that are controlled and maintained by third parties including Suppliers
        or Specifiers (“Third Parties”). If you decide to visit any Third Party
        Property or use a service made available through, or purchase products
        via, a Third Party Property, you do so at your own risk. It is your
        responsibility to satisfy yourself as to the reputation of the relevant
        Third Party and the services they offer. We are not responsible for the
        content, accuracy or opinions expressed on Third Party Properties. Links
        that we make available do not imply that we are, or our Jammable
        Platform is, affiliated to or associated with such websites or services.
        <div className="subtitle">17. Limitations of liability</div>
        17.1 You have certain legal rights when using the Jammable Platform.
        These are also known as “statutory rights” as they are derived from laws
        such as the UK Consumer Rights Act 2015. Nothing in these Terms affects
        these rights.
        <br />
        <br />
        17.2 We do not exclude or limit in any way our liability to you where it
        would be unlawful to do so. This includes liability for death or
        personal injury caused by our negligence or the negligence of our
        employees, agents or subcontractors and for fraud or fraudulent
        misrepresentation.
        <br />
        <br />
        17.3 All warranties, representations, conditions and all other terms of
        any kind whatsoever implied by statute or common law are excluded from
        these Terms to the fullest extent permitted by law.
        <br />
        <br />
        17.4 Subject to this section 17 and any refunds owed to you in
        accordance with these Terms, our total aggregate liability under or in
        connection with your use of the Jammable Platform, the Services or any
        matter arising under or in connection with these Terms shall be limited
        to the greater of: (i) Subscription Fees paid by you in the 12 months
        immediately preceding the month during which the liability arose; or
        (ii) £100.
        <br />
        <br />
        17.5 Jammable is responsible for losses you suffer caused by us breaking
        this contract unless the loss is:
        <ol style={{ listStyleType: "lower-alpha" }}>
          <li>
            Unexpected. It was not obvious that it would happen and nothing you
            said to us before you accepted these Terms meant we should have
            expected it (so, in the law, the loss was unforeseeable);
          </li>
          <li>
            Caused by a delaying event outside our control. As long as we have
            taken the steps set out in section 18.2;
          </li>
          <li>Not caused by our breach of these Terms or our negligence;</li>
          <li>Caused by your use of the Platform in breach of these Terms;</li>
          <li>
            Avoidable. Something you could have avoided by taking reasonable
            action. For example, damage to your own device, which was caused by
            the Jammable Platform and which you could have avoided by following
            our advice; or
          </li>
          <li>
            A business loss. It relates to your use of a product for the
            purposes of your trade, business, craft or profession.
          </li>
        </ol>
        <div className="subtitle">
          18. Other important terms that apply to our contract
        </div>
        18.1 We can transfer our contract with you, so that a different
        organisation is responsible for supplying the Services. We’ll contact
        you to let you know if we plan to do this. If you’re unhappy with the
        transfer you can cancel your subscription by logging into your Account
        and follow the instructions for cancellation on the Settings page and we
        will refund you any payments you’ve made in advance for any Service not
        provided. You can only transfer your contract with us to someone else if
        we agree to this.
        <br />
        <br />
        18.2 We’re not responsible for delays outside our control. Jammable will
        not be liable to you for any failure to fulfil its duties under these
        Terms if and to the extent that such failure results from an act or
        event affecting the performance by Jammable of its obligations under
        these Terms arising from events beyond its reasonable control
        (including, without limitation, natural disaster, pandemic, fire, flood,
        terrorist attack or an industrial dispute) we contact you as soon as
        possible to let you know and do what we can to reduce the delay. As long
        as we do this, we won’t compensate you for the delay, but if the delay
        is likely to be substantial you can cancel your subscription and contact
        our Support Team at support@jammable.com to receive a refund for any
        Service you have paid for in advance, but not received, less reasonable
        costs we have already incurred.
        <br />
        <br />
        18.3 Even if we delay in enforcing this contract, we can still enforce
        it later. We might not immediately chase you for not doing something
        (like paying) or for doing something you’re not allowed to, but that
        doesn’t mean we can’t do it later.
        <br />
        <br />
        18.4 If a court invalidates some of this contract, the rest of it will
        still apply. If a court or other authority decides that some of these
        terms are unlawful, the rest will continue to apply.
        <br />
        <br />
        18.5 Nobody else has any rights under this contract. This contract is
        between you and us. Nobody else can enforce it and neither of us will
        need to ask anybody else to sign-off on ending or changing it.
        <br />
        <br />
        18.6 Governing Law and Jurisdiction. These Terms and any dispute or
        claim arising out of or in connection with them, their subject matter or
        their formation (including non-contractual disputes or claims) shall be
        governed by and construed in accordance with English law. As a consumer,
        you may have a right to bring a claim in your local jurisdiction. In all
        other cases, the courts of England and Wales will have exclusive
        jurisdiction to settle any dispute which may arise out of or in
        connection with these Terms.
        <div className="subtitle">19. Notice</div>
        All notices given by you to us may be given to us at Kalculus, 119
        Marylebone Road, London, United Kingdom, NW1 5PU or via e-mail at
        team@jammable.com. We will give notice to you via the Jammable Platform
        or using the contact information you provided to us as part of your
        registration.
        <div className="subtitle">20. Raising concerns</div>
        20.1 If you have an enquiry, comment or complaint about Jammable
        Platform or any of our Services, please submit a support request to our
        Support Team at support@jammable.com.
        <br />
        <br />
        20.2 If we are unable to resolve your complaint and you wish to raise a
        dispute or bring a claim against us, you may first seek to resolve this
        informally with us and if this is not possible, we will offer to engage
        in an impartial out-of-court dispute resolution procedure with you.
        <br />
        <br />
        Reporting claims of copyright infringement. We take claims of copyright
        infringement seriously. We will respond to notices of alleged copyright
        infringement that comply with applicable law. If you believe any
        materials accessible on or from the Jammable Platform infringe your
        copyright, you may request removal of those materials (or access to
        them) from the Jammable Platform by submitting written notification to
        our copyright agent designated in our{" "}
        <a href="https://www.jammable.com/dmca-policy">
          DMCA & Copyright Policy.
        </a>
      </div>
    </div>
  );
};

export default TermsPage;
